// src/components/Calendar/CalendarVoteForm.jsx
import {
    Box, Typography, TextField, Button, IconButton,
    Switch, FormControlLabel, Stack, Divider, Card, CardContent,
    Grid
} from '@mui/material';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import dayjs from 'dayjs';

const CalendarVoteForm = ({ onChange, isEdit = false, initialVoteData = null }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [voteQuestion, setVoteQuestion] = useState('');
    const [voteOptions, setVoteOptions] = useState(['', '']);
    const [voteDate, setVoteDate] = useState(dayjs().add(3, 'day'));
    const [voteTime, setVoteTime] = useState(dayjs().hour(23).minute(59));
    const [voteDeadline, setVoteDeadline] = useState(
        dayjs().add(3, 'day').hour(23).minute(59)
    );
    const [allowMultiple, setAllowMultiple] = useState(false);
    const [isAnonymousVote, setIsAnonymousVote] = useState(false);
    const [hasVoteRecord, setHasVoteRecord] = useState(false);
    const [openVoteForm, setOpenVoteForm] = useState(false);

    const lastSentData = useRef('');
    const didInit = useRef(false);

    useEffect(() => {
        if (!didInit.current && initialVoteData) {
            didInit.current = true;

            /* ── ① 질문 / 선택지: 두 이름 모두 수용 ── */
            setVoteQuestion(
                initialVoteData.vote_question ?? initialVoteData.question ?? ''
            );
            setVoteOptions(
                initialVoteData.vote_options ?? initialVoteData.options ?? ['', '']
            );

            /* ── ② 마감 날짜·시간: vote_deadline ↔ deadline 모두 수용 ── */
            const rawDeadline =
                initialVoteData.vote_deadline ?? initialVoteData.deadline;
            if (rawDeadline) {
                const initDeadline = dayjs(rawDeadline);
                setVoteDate(initDeadline.startOf('day'));
                setVoteTime(initDeadline);
                setVoteDeadline(initDeadline);
            }

            /* ── ③ 기타 옵션은 그대로 ── */
            setAllowMultiple(initialVoteData.allow_multiple || false);
            setIsAnonymousVote(
                initialVoteData.is_anonymous ??
                initialVoteData.anonymous ??      /* 혹시 다른 이름 대비 */
                false
            );
            setHasVoteRecord(initialVoteData.has_vote_record || false);
            setOpenVoteForm(true);
        }
    }, [initialVoteData]);

    useEffect(() => {
        if (!openVoteForm) return;
        const trimmedOptions = voteOptions.map((o) => o.trim()).filter((o) => o);
        const currentData = JSON.stringify({
            has_vote: true,
            vote_question: voteQuestion.trim(),
            vote_options: trimmedOptions,
            vote_deadline: voteDeadline.format('YYYY-MM-DD HH:mm:ss'),
            allow_multiple: allowMultiple,
            is_anonymous: isAnonymousVote,
        });

        if (lastSentData.current !== currentData) {
            lastSentData.current = currentData;
            onChange(JSON.parse(currentData));
        }
    }, [
        voteQuestion,
        voteOptions,
        voteDeadline,
        allowMultiple,
        isAnonymousVote,
        onChange,
        openVoteForm,
        hasVoteRecord,
    ]);

    const handleOptionChange = (idx, value) => {
        const updated = [...voteOptions];
        updated[idx] = value;
        setVoteOptions(updated);
    };

    const addOption = () => {
        if (voteOptions.length >= 10) {
            alert('선택지는 최대 10개까지 가능합니다.');
            return;
        }
        setVoteOptions([...voteOptions, '']);
    };

    const removeOption = (idx) => {
        if (hasVoteRecord) return;
        if (voteOptions.length <= 2) {
            alert('선택지는 최소 2개 이상이어야 합니다.');
            return;
        }
        const updated = [...voteOptions];
        updated.splice(idx, 1);
        setVoteOptions(updated);
    };

    return (
        <Box sx={{ mt: 4 }}>
            <Box sx={{ textAlign: 'left', mb: 2 }}>
                <Button
                    variant={openVoteForm ? 'contained' : 'outlined'}
                    startIcon={<HowToVoteIcon />}
                    endIcon={
                        <ExpandMoreIcon
                            sx={{
                                transform: openVoteForm ? 'rotate(180deg)' : 'none',
                                transition: '.2s',
                            }}
                        />
                    }
                    onClick={() => setOpenVoteForm(!openVoteForm)}
                    disabled={hasVoteRecord}
                    fullWidth={isMobile}
                >
                    {openVoteForm ? '투표 접기' : '투표 추가하기'}
                </Button>
            </Box>

            {openVoteForm && (
                <Card variant="outlined">
                    <CardContent>
                        <Stack spacing={3}>
                            {hasVoteRecord && (
                                <Typography variant="body2" color="text.secondary">
                                    ❗ 이미 투표자가 있어 마감 날짜 외 설정은 수정할 수 없습니다.
                                </Typography>
                            )}

                            <TextField
                                fullWidth
                                label="투표 질문"
                                value={voteQuestion}
                                onChange={(e) => setVoteQuestion(e.target.value)}
                                disabled={hasVoteRecord}
                            />

                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    선택지
                                </Typography>
                                <Stack spacing={1}>
                                    {voteOptions.map((opt, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                        >
                                            <TextField
                                                fullWidth
                                                label={`선택지 ${idx + 1}`}
                                                value={opt}
                                                onChange={(e) => handleOptionChange(idx, e.target.value)}
                                                disabled={hasVoteRecord}
                                            />
                                            {!hasVoteRecord && voteOptions.length > 2 && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => removeOption(idx)}
                                                    color="error"
                                                    sx={{ width: 28, height: 28 }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    ))}
                                    {!hasVoteRecord && (
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={addOption}
                                            sx={{ mt: 1 }}
                                        >
                                            선택지 추가
                                        </Button>
                                    )}
                                </Stack>
                            </Box>

                            {!hasVoteRecord && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={allowMultiple}
                                                    onChange={(e) => setAllowMultiple(e.target.checked)}
                                                />
                                            }
                                            label="복수 선택 허용"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={isAnonymousVote}
                                                    onChange={(e) => setIsAnonymousVote(e.target.checked)}
                                                />
                                            }
                                            label="익명 투표"
                                        />
                                    </Grid>
                                </Grid>
                            )}

                            {!hasVoteRecord && <Divider />}

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <DatePicker
                                            label="마감 날짜"
                                            value={voteDate}
                                            onChange={(newVal) => {
                                                const combined = newVal
                                                    ?.hour(voteTime.hour())
                                                    .minute(voteTime.minute())
                                                    .second(0);

                                                if (combined.isBefore(dayjs())) {
                                                    alert('⚠️ 마감 날짜는 현재 시각보다 이후여야 합니다.');
                                                    return;
                                                }
                                                setVoteDate(newVal);
                                                setVoteDeadline(combined);  // ✅ 직접 갱신
                                            }}
                                            minDate={dayjs().startOf('day')}
                                            format="YYYY/MM/DD"
                                            slotProps={{ textField: { fullWidth: true } }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TimePicker
                                            label="마감 시간"
                                            value={voteTime}
                                            onChange={(newVal) => {
                                                const combined = voteDate
                                                    ?.hour(newVal.hour())
                                                    .minute(newVal.minute())
                                                    .second(0);

                                                if (combined.isBefore(dayjs())) {
                                                    alert('⚠️ 마감 시각은 현재 시각보다 이후여야 합니다.');
                                                    return;
                                                }
                                                setVoteTime(newVal);
                                                setVoteDeadline(combined);  // ✅ 직접 갱신
                                            }}
                                            ampm
                                            slotProps={{ textField: { fullWidth: true } }}
                                        />
                                    </Grid>
                                </Grid>
                            </LocalizationProvider>
                        </Stack>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default CalendarVoteForm;
